import { authHeaders, ensureFreshToken, clearExpiredSession } from './core/auth';
import { STORAGE_API } from './core/config';
import { isOfficeDocumentName } from './core/helpers';

/** Primary backend is R2 presigned when server supports it; legacy name kept for callers. */
export const isAzureStorage = () => false;

export async function listClientDocs(prefix) {
  await ensureFreshToken();
  const res = await fetch(`${STORAGE_API}/list`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ prefix }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'List failed');
  return Array.isArray(data) ? data.filter((f) => f.name) : [];
}

async function uploadViaProxy(blobPath, file, contentType, fallbackError) {
  const res = await fetch(`${STORAGE_API}/upload/${blobPath}`, {
    method: 'PUT',
    headers: { ...authHeaders(), 'Content-Type': contentType },
    body: file,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || fallbackError || `Upload failed (${res.status})`);
  return data;
}

async function confirmPresignedUpload(blobPath, backend) {
  const confirmRes = await fetch(`${STORAGE_API}/confirm`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: blobPath, backend: backend || 'r2' }),
  });
  const confirmJson = await confirmRes.json().catch(() => ({}));
  if (!confirmRes.ok) throw new Error(confirmJson.error || 'Upload confirm failed');
  return confirmJson.data || { path: blobPath, backend: backend || 'r2' };
}

/**
 * Upload via presigned PUT (direct to R2). Falls back to server proxy if presign or direct PUT fails.
 * Master R2 keys never ship in the mobile/web bundle.
 */
export async function uploadClientDoc(blobPath, file) {
  await ensureFreshToken();
  const contentType = file.type || 'application/octet-stream';

  const presignRes = await fetch(`${STORAGE_API}/presign-upload`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: blobPath, contentType }),
  });
  const presignJson = await presignRes.json().catch(() => ({}));

  if (presignRes.ok && presignJson.data?.uploadUrl) {
    const { uploadUrl, backend } = presignJson.data;
    let directOk = false;
    try {
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file,
      });
      directOk = putRes.ok;
    } catch (_) {
      // ponytail: R2 bucket CORS missing site origin — proxy upload until R2 CORS is updated
    }
    if (directOk) return confirmPresignedUpload(blobPath, backend);
  }

  return uploadViaProxy(blobPath, file, contentType, presignJson.error);
}

/** Short-lived presigned GET URL (authenticated). Prefer for admin doc viewers. */
export async function fetchPresignedDocUrl(blobPath, downloadName) {
  await ensureFreshToken();
  const res = await fetch(`${STORAGE_API}/presign-download`, {
    method: 'POST',
    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
    body: JSON.stringify({ path: blobPath, download: downloadName }),
  });
  const json = await res.json().catch(() => ({}));
  if (res.status === 401) {
    clearExpiredSession();
    throw new Error(json.error || 'Invalid or expired session');
  }
  if (!res.ok) throw new Error(json.error || 'Could not get document URL');
  return json.data?.url;
}

/** Infer R2/storage key from a stored portal URL (submissions often keep url but drop path). */
export function storagePathFromUrl(url) {
  if (!url) return null;
  const s = String(url);
  const patterns = [
    /\/storage\/url\/([^?#]+)/,
    /\/storage\/file\/([^?#]+)/,
    /\/storage\/download\/([^?#]+)/,
  ];
  for (const re of patterns) {
    const m = s.match(re);
    if (m) {
      try { return decodeURIComponent(m[1]); } catch { return m[1]; }
    }
  }
  if (/r2\.cloudflarestorage\.com/i.test(s)) {
    try {
      const u = new URL(s);
      const host = u.hostname.toLowerCase();
      const parts = u.pathname.replace(/^\/+/, '').split('/').filter(Boolean);
      if (!parts.length) return null;
      // Path-style: {accountId}.r2.cloudflarestorage.com/{bucket}/{key...}
      if (/^[a-f0-9]{32}\.r2\.cloudflarestorage\.com$/i.test(host)) {
        if (parts.length >= 2) return decodeURIComponent(parts.slice(1).join('/'));
        return null;
      }
      // Virtual-hosted: {bucket}.{accountId}.r2.cloudflarestorage.com/{key...}
      return decodeURIComponent(parts.join('/'));
    } catch (_) { /* ignore */ }
  }
  return null;
}

function isInlineDocUrl(url) {
  const s = String(url || '');
  return s.startsWith('data:') || s.startsWith('blob:');
}

function isRemoteStorageUrl(url) {
  const s = String(url || '');
  return /^https?:\/\//i.test(s) && !s.includes('/api/storage/');
}

/** Match R2 key from upload pattern: companyId/taskId/dirIdx_slotId_timestamp_file */
export function findUploadedFilePath(files, companyId, taskId, dirIdx, slotId) {
  if (!companyId || dirIdx == null || !slotId) return null;
  const marker = `${dirIdx}_${slotId}_`;
  const hit = (files || []).find((f) => {
    const p = f.path || '';
    if (!p.startsWith(`${companyId}/`) || !p.includes(marker)) return false;
    if (taskId) {
      return p.startsWith(`${companyId}/${taskId}/${marker}`) || p.includes(`/${taskId}/${marker}`);
    }
    return true;
  });
  return hit?.path || null;
}

function stripDocExtension(name) {
  return String(name || '').replace(/\.[^.]+$/i, '');
}

function humanizeStorageFileName(raw) {
  const base = String(raw || '').split('/').pop() || '';
  const m = base.match(/^\d+_[a-f0-9-]+_\d+_(.+)$/i);
  if (m) return m[1].replace(/_/g, ' ');
  return base.replace(/^\d+_/, '');
}

/** Match R2 key when submission kept name but not path (common on client portal). */
export function findFilePathByNameHint(files, nameHint) {
  const hint = stripDocExtension(nameHint || '').toLowerCase();
  if (!hint) return null;
  const hit = (files || []).find((f) => {
    const base = stripDocExtension(humanizeStorageFileName(f.name || f.path || '')).toLowerCase();
    return base === hint || base.includes(hint) || hint.includes(base);
  });
  return hit?.path || null;
}

/** Resolve blob path from ref + optional R2 list / upload-slot context. */
export async function resolveStorageBlobPath(ref, {
  storagePath,
  listPrefix,
  companyId,
  taskId,
  dirIdx,
  slotId,
  files,
} = {}) {
  if (storagePath) return storagePath;
  const fromUrl = storagePathFromUrl(ref?.url);

  let pool = files;
  const prefixes = [];
  if (listPrefix) prefixes.push(listPrefix);
  if (companyId && listPrefix !== `${companyId}/`) prefixes.push(`${companyId}/`);
  for (const pfx of prefixes) {
    if (pool?.length) break;
    try {
      pool = await listClientDocs(pfx);
    } catch { pool = []; }
  }

  const fromUpload = findUploadedFilePath(pool, companyId, taskId, dirIdx, slotId);
  if (fromUpload) return fromUpload;

  const fromSlotAnyTask = findUploadedFilePath(pool, companyId, null, dirIdx, slotId);
  if (fromSlotAnyTask) return fromSlotAnyTask;

  const fromName = findFilePathByNameHint(pool, ref?.name);
  if (fromName) return fromName;

  if (ref?.path) return ref.path;
  if (fromUrl) return fromUrl;

  return null;
}

function encodeBlobPath(blobPath) {
  return String(blobPath || '')
    .split('/')
    .map((part) => encodeURIComponent(part))
    .join('/');
}

function mimeFromFileName(name = '') {
  const ext = String(name).split('.').pop()?.toLowerCase();
  const map = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
    txt: 'text/plain',
  };
  return map[ext] || 'application/octet-stream';
}

function blobForDisplay(blob, contentType, fileName) {
  const mime = (contentType && !contentType.includes('octet-stream'))
    ? contentType.split(';')[0].trim()
    : mimeFromFileName(fileName);
  if (!mime || blob.type === mime) return blob;
  return new Blob([blob], { type: mime });
}

/** Authenticated GET stream — works with R2 on Capacitor (no presigned CORS). */
export async function fetchAuthenticatedDocBlob(blobPath) {
  await ensureFreshToken();
  const res = await fetch(`${STORAGE_API}/download/${encodeBlobPath(blobPath)}`, {
    method: 'GET',
    headers: authHeaders(),
  });
  if (res.status === 401) {
    clearExpiredSession();
    throw new Error('Invalid or expired session');
  }
  if (!res.ok) {
    const json = await res.json().catch(() => ({}));
    throw new Error(json.error || `Could not load document (${res.status})`);
  }
  const contentType = res.headers.get('content-type') || '';
  const blob = await res.blob();
  return { blob, contentType };
}

/** Redirect through API (legacy). For R2, server issues presigned redirect. */
export function publicDocUrl(blobPath, downloadName) {
  const base = `${STORAGE_API}/url/${blobPath}`;
  return downloadName ? `${base}?download=${encodeURIComponent(downloadName)}` : base;
}

export function resolveDocSrc(docObj) {
  if (!docObj) return null;
  if (typeof docObj === 'string') return docObj;
  if (typeof docObj !== 'object') return null;

  const direct = docObj.url || docObj.dataUrl || docObj.data_url || docObj.base64 || docObj.file || null;
  if (direct) return direct;
  if (docObj.path) return publicDocUrl(docObj.path, docObj.name);
  return null;
}

/** Normalize list/submission/doc slot objects into { path, name, url, ... }. */
export function normalizeDocRef(docObj, fallbackName) {
  if (!docObj) return null;
  if (typeof docObj === 'string') return { url: docObj, name: fallbackName || "document" };
  const name = docObj.name || fallbackName
    || (docObj.path && String(docObj.path).split("/").pop()?.replace(/^\d+_/, ""))
    || "document";
  const path = docObj.path || storagePathFromUrl(docObj.url) || null;
  return { ...docObj, name, path };
}

/** Fresh presigned URL when possible (legacy fallback — prefer fetchAuthenticatedDocBlob on web). */
export async function resolveDocumentUrl(docObj, { download = false } = {}) {
  const ref = normalizeDocRef(docObj);
  if (!ref) return null;
  const name = ref.name || "document";
  const inline = ref.url || ref.dataUrl || ref.data_url || ref.base64 || ref.file;
  if (inline && isInlineDocUrl(inline)) return inline;

  if (ref.path) {
    try {
      return await fetchPresignedDocUrl(ref.path, download ? name : undefined);
    } catch {
      return publicDocUrl(ref.path, download ? name : undefined);
    }
  }

  if (inline && !isRemoteStorageUrl(inline)) return inline;
  return null;
}

async function fetchDocBlobForRef(ref, ctx) {
  const blobPath = await resolveStorageBlobPath(ref, ctx);
  if (!blobPath) return null;

  const tryFetch = async (path) => {
    const { blob, contentType } = await fetchAuthenticatedDocBlob(path);
    return { blob, contentType, blobPath: path };
  };

  try {
    return await tryFetch(blobPath);
  } catch (firstErr) {
    if (!ctx?.companyId) throw firstErr;
    let pool = ctx.files;
    if (!pool?.length) {
      try {
        pool = await listClientDocs(`${ctx.companyId}/`);
      } catch { pool = []; }
    }
    const alt = findUploadedFilePath(pool, ctx.companyId, ctx.taskId, ctx.dirIdx, ctx.slotId)
      || findUploadedFilePath(pool, ctx.companyId, null, ctx.dirIdx, ctx.slotId)
      || findFilePathByNameHint(pool, ref?.name);
    if (alt && alt !== blobPath) return tryFetch(alt);
    throw firstErr;
  }
}

function triggerBlobDownload(blob, name) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = name || 'document';
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(objectUrl), 30_000);
}

function escapeHtml(s) {
  return String(s || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

const PREVIEW_CARD_LAYOUT =
  '*{box-sizing:border-box}'
  + '.card{background:rgba(255,255,255,.97);color:#0B1F3A;border-radius:16px;padding:28px 22px;max-width:360px;width:100%;'
  + 'text-align:center;box-shadow:0 20px 50px rgba(0,0,0,.25);overflow:hidden;min-width:0}'
  + '.fname,.msg{overflow-wrap:anywhere;word-break:break-word;hyphens:auto;max-width:100%}';

/** Open preview tab immediately on user click with loader (call before async fetch). */
export function openDocumentPreviewTab(fileName = 'document') {
  const tab = window.open('', '_blank');
  if (!tab) return null;
  const title = escapeHtml(fileName);
  tab.document.write(
    '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">'
    + `<title>${title} · Founders Bridge</title>`
    + '<style>'
    + 'body{margin:0;min-height:100vh;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif;'
    + 'background:linear-gradient(165deg,#0B1F3A 0%,#132d52 45%,#0f2744 100%);color:#e8eef5;display:flex;align-items:center;justify-content:center;padding:24px}'
    + PREVIEW_CARD_LAYOUT
    + '.spin{width:44px;height:44px;margin:0 auto 20px;border:3px solid #e5e7eb;border-top-color:#0B1F3A;border-radius:50%;animation:fbspin .75s linear infinite}'
    + '@keyframes fbspin{to{transform:rotate(360deg)}}'
    + 'h1{font-size:15px;font-weight:700;margin:0 0 8px;line-height:1.35}'
    + 'p{margin:0;font-size:13px;color:#6b7280;line-height:1.5}'
    + '</style></head><body><div class="card" role="status" aria-live="polite">'
    + '<div class="spin" aria-hidden="true"></div>'
    + '<h1>Opening document</h1>'
    + `<p class="fname">${title}</p><p class="msg" style="margin-top:12px">Loading preview…</p>`
    + '</div></body></html>',
  );
  tab.document.close();
  return tab;
}

function writePreviewTab(tab, { pageTitle, heading, bodyHtml, theme = 'error' }) {
  if (!tab || tab.closed) return;
  const bg = theme === 'info' ? '#eff6ff' : '#fef2f2';
  const border = theme === 'info' ? '#bfdbfe' : '#fecaca';
  const hColor = theme === 'info' ? '#1e40af' : '#991b1b';
  tab.document.open();
  tab.document.write(
    '<!DOCTYPE html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">'
    + `<title>${escapeHtml(pageTitle)}</title>`
    + `<style>body{margin:0;min-height:100vh;font-family:system-ui,sans-serif;display:flex;align-items:center;justify-content:center;background:${bg};padding:24px}`
    + `.card{background:#fff;border:1px solid ${border};border-radius:12px;padding:24px 20px;max-width:400px;width:100%;overflow:hidden;min-width:0}`
    + `h1{font-size:16px;color:${hColor};margin:0 0 8px;line-height:1.35}`
    + '.msg{font-size:13px;color:#444;margin:0;line-height:1.55;overflow-wrap:anywhere;word-break:break-word}'
    + '.fname{font-size:12px;color:#6b7280;margin:0 0 12px;overflow-wrap:anywhere;word-break:break-word}</style></head><body>'
    + `<div class="card"><h1>${escapeHtml(heading)}</h1>${bodyHtml}</div></body></html>`,
  );
  tab.document.close();
}

export function showDocumentPreviewTabError(tab, message) {
  const msg = escapeHtml(message || 'Could not open document');
  writePreviewTab(tab, {
    pageTitle: 'Preview unavailable',
    heading: 'Preview unavailable',
    bodyHtml: `<p class="msg">${msg}</p>`,
    theme: 'error',
  });
}

export function showDocumentPreviewTabOfficeNotice(tab, fileName = 'document') {
  const title = escapeHtml(fileName);
  writePreviewTab(tab, {
    pageTitle: 'Download to view',
    heading: 'Cannot preview in browser',
    bodyHtml:
      `<p class="fname">${title}</p>`
      + '<p class="msg">Word, Excel, and PowerPoint files cannot be opened in the browser. '
      + 'Close this tab, return to Founders Bridge, and use <strong>Download</strong> to save the file, then open it on your device.</p>',
    theme: 'info',
  });
}

/** @param {Window|null} preOpenedWindow — open synchronously on click before async fetch (avoids blank/blocked popups) */
function openBlobInNewTab(blob, fileName = 'document', contentType = '', preOpenedWindow = null) {
  const typed = blobForDisplay(blob, contentType || blob.type, fileName);
  const objectUrl = URL.createObjectURL(typed);
  const mime = typed.type || '';

  const tab = preOpenedWindow && !preOpenedWindow.closed ? preOpenedWindow : null;
  if (tab) {
    if (mime.startsWith('image/')) {
      const title = escapeHtml(fileName);
      tab.document.open();
      tab.document.write(
        `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">`
        + `<title>${title}</title>`
        + '<style>body{margin:0;min-height:100vh;background:#111;display:flex;justify-content:center;align-items:flex-start}'
        + 'img{max-width:100%;height:auto;display:block}</style></head>'
        + `<body><img src="${objectUrl}" alt="${title}"/></body></html>`,
      );
      tab.document.close();
    } else {
      tab.location.href = objectUrl;
    }
  } else {
    const a = document.createElement('a');
    a.href = objectUrl;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  setTimeout(() => URL.revokeObjectURL(objectUrl), 120_000);
  return objectUrl;
}

let _lastViewAt = 0;
let _lastViewUrl = "";

export async function viewDocument(docObj, fallbackName, ctx, preOpenedWindow = null) {
  const ref = normalizeDocRef(docObj, fallbackName);
  if (!ref) throw new Error('Could not open document');
  const name = ref.name || fallbackName || 'document';

  if (isOfficeDocumentName(name)) {
    showDocumentPreviewTabOfficeNotice(preOpenedWindow, name);
    throw new Error('Office documents cannot be previewed in the browser. Use Download instead.');
  }

  const inline = ref.url || ref.dataUrl || ref.data_url || ref.base64 || ref.file;
  if (inline && isInlineDocUrl(inline)) {
    const b = await (await fetch(inline)).blob();
    return openBlobInNewTab(b, name, b.type, preOpenedWindow);
  }

  try {
    const hit = await fetchDocBlobForRef(ref, ctx);
    if (hit?.blob) {
      return openBlobInNewTab(hit.blob, name, hit.contentType, preOpenedWindow);
    }
  } catch (e) {
    const msg = e.message || 'Could not open document';
    showDocumentPreviewTabError(preOpenedWindow, msg);
    throw new Error(msg);
  }
  showDocumentPreviewTabError(preOpenedWindow, 'Could not open document');
  throw new Error('Could not open document');
}

export async function downloadDocument(docObj, fallbackName, ctx) {
  const ref = normalizeDocRef(docObj, fallbackName);
  const name = ref?.name || fallbackName || 'document';

  const inline = ref.url || ref.dataUrl || ref.data_url || ref.base64 || ref.file;
  if (inline && isInlineDocUrl(inline)) {
    triggerBlobDownload(await (await fetch(inline)).blob(), name);
    return inline;
  }

  try {
    const hit = await fetchDocBlobForRef(ref, ctx);
    if (hit?.blob) {
      triggerBlobDownload(blobForDisplay(hit.blob, hit.contentType, name), name);
      return hit.blobPath;
    }
  } catch (e) {
    throw new Error(e.message || 'Could not download document');
  }
  throw new Error('Could not download document');
}
